/**
 * The journal index (/blog), one renderer for the server and the client
 * (2026-10-05).
 *
 * A direct load got the server's list: every post from the data, newest
 * first, dates in the page's language, links in the locale where the post is
 * translated. An in-app navigation got the client's own page: a hand-kept
 * list of 63 slugs with English dates typed in, a "featured" post, three
 * filter categories that matched none of the four rubrics, a destinations
 * block and English links. The server's headings were a separate copy too
 * (the es, pt and tr h1 was English). Both now print this markup from the
 * catalog and the post data; the hero is the listing hero /prices and /faq use.
 */
import { localeHref } from './locale-href.js';
import { ledeHtml } from './rich-text.js';
import { formatPostDate } from './country-page.js';
import { normalizeReadTime } from './blog-post.js';
function esc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
/** Everything inside `<main>`: the journal index. */
export function blogIndexHtml({ locale, t, posts, hasBlogTranslation }) {
    const href = (slug) => locale !== 'en' && !hasBlogTranslation(slug) ? `/blog/${slug}` : localeHref(locale, `/blog/${slug}`);
    // Newest first; a post without a date goes last.
    const sorted = [...posts].sort(([, a], [, b]) => {
        if (!a.date)
            return 1;
        if (!b.date)
            return -1;
        return b.date.localeCompare(a.date);
    });
    const cards = sorted.map(([slug, p]) => {
        const readTime = normalizeReadTime(p.readTime);
        // Date first: on a wide screen the meta is the row's left rail, a
        // journal's table of contents; on a phone it runs inline above the title
        // (the separators come from CSS).
        const meta = [
            p.date ? `<time datetime="${esc(p.date)}">${esc(formatPostDate(p.date, locale))}</time>` : '',
            p.category ? `<span class="blog-card__category">${esc(p.category)}</span>` : '',
            readTime ? `<span class="blog-card__readtime">${esc(readTime)}</span>` : '',
        ].join('');
        return `<article class="blog-card">
      <a class="blog-card__link" href="${esc(href(slug))}">
        ${meta ? `<div class="blog-card__meta">${meta}</div>` : ''}
        <h2 class="blog-card__title">${esc(p.h1 ?? p.title ?? slug)}</h2>
        ${p.lede ? `<p class="blog-card__lede">${ledeHtml(p.lede)}</p>` : ''}
      </a>
    </article>`;
    }).join('\n');
    return `
<article class="blog-index-route">
<section class="hero listing-hero">
  <div class="container">
    <div class="hero__masthead">
      <div class="hero__volume">${esc(t('blogIndex.hero.volume'))}</div>
      <div class="hero__edition">${esc(t('blogIndex.hero.edition'))}</div>
      <div class="hero__date">MMXXVI</div>
    </div>
    <div class="listing-hero__body">
      <div class="section__kicker">${esc(t('blogIndex.hero.kicker'))}</div>
      <h1 class="listing-hero__title">${esc(t('blogIndex.hero.title'))}</h1>
      <p class="listing-hero__lede">${esc(t('blogIndex.hero.lede'))}</p>
    </div>
  </div>
</section>
<section class="blog-index-list-section">
  <div class="container">
    <div class="blog-index-list">
      ${cards}
    </div>
  </div>
</section>
</article>`;
}
